import { TFunction } from '@spotify-internal/i18n-core';
import { DecisionReason, TrackDetails } from '@hooks/useTrackInfo/types';
import { EMPTY_VALUE } from '@helpers/constants';

export const getReasonTranslationKey = (
  reason: DecisionReason,
  t: TFunction,
) => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
      return t(
        'atlas.unassociated_videos.reason.block_patch',
        'Track not eligible for association',
        'Reason for an unassociated video that is not eligible for associations',
      );
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
    case DecisionReason.NO_ASSOCIATION_CANDIDATES:
      return t(
        'atlas.unassociated_videos.reason.no_release_candidates',
        'No related release candidates',
        'Reason for an unassociated video that does not has an release candidate',
      );
    case DecisionReason.TRACK_NAME_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.name_mismatch',
        'Track name mismatch',
        'Reason for an unassociated video that has a name mismatch between the track and the video',
      );
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.artist_mismatch',
        'Track artist mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their artists',
      );
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.explicitness_mismatch',
        'Track explicitness mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their explicitness',
      );
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.duration_mismatch',
        'Track duration mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their duration',
      );
    case DecisionReason.TRACK_LANGUAGE_OF_PERFORMANCE_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.language_mismatch',
        'Track language of performance mismatch',
        'Reason for an unassociated video that has a language mismatch between the track and the video',
      );
    case DecisionReason.FINGER_PRINT_CANDIDATE_MATCH:
      return t(
        'atlas.unassociated_videos.reason.fingerprint_match',
        'Fingerprint candidate match',
        'Reason for an associated video that matched via fingerprint',
      );
    case DecisionReason.RELATED_RELEASE_CANDIDATE_MATCH:
      return t(
        'atlas.unassociated_videos.reason.related_release_match',
        'Related release candidate match',
        'Reason for an associated video that matched via related release candidate',
      );
    case DecisionReason.RELATED_RELEASE_EXPANSION_MATCH:
      return t(
        'atlas.unassociated_videos.reason.related_release_expansion_match',
        'Related release expansion match',
        'Reason for an associated video that matched via related release expansion',
      );
    case DecisionReason.FINGER_PRINT_EXPANSION_MATCH:
      return t(
        'atlas.unassociated_videos.reason.fingerprint_expansion_match',
        'Fingerprint expansion match',
        'Reason for an associated video that matched via fingerprint expansion',
      );
    case DecisionReason.UNCATEGORIZED_MATCH:
      return t(
        'atlas.unassociated_videos.reason.uncategorized_match',
        'Uncategorized match',
        'Reason when reason for association is unknown',
      );
    default:
      return t(
        'atlas.unassociated_videos.reason.unknown',
        'Unknown',
        'Unknown reason for an unassociated video',
      );
  }
};

export const getReasonDescriptionTranslationKey = (
  reason: DecisionReason,
  t: TFunction,
) => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
      return t(
        'atlas.unassociated_videos.reason_description.block_patch',
        'One or both tracks are not currently eligible for association.',
        'Reason description for when an unassociated video is not eligible for associations',
      );
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
    case DecisionReason.NO_ASSOCIATION_CANDIDATES:
      return t(
        'atlas.unassociated_videos.reason_description.no_release_candidates',
        "The Related Release ISRC provided was not matched to a video in your catalog. Please ensure it's not a duplicate of the Video ISRC and that it is a valid ISRC.",
        'Reason description for when an unassociated video does not has an release candidate',
      );
    case DecisionReason.TRACK_NAME_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.name_mismatch',
        "Both tracks' names should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track and the video',
      );
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.artist_mismatch',
        "Both tracks' contributing artists and roles should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their artists',
      );
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.explicitness_mismatch',
        "Both tracks' explicitness should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their explicitness',
      );
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.duration_mismatch',
        "Both tracks' durations should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their duration',
      );
    case DecisionReason.TRACK_LANGUAGE_OF_PERFORMANCE_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.language_mismatch',
        "Both tracks' language of performance should be an exact match.",
        'Reason description for when an unassociated video has a language mismatch between the track and the video',
      );
    case DecisionReason.FINGER_PRINT_CANDIDATE_MATCH:
      return t(
        'atlas.unassociated_videos.reason_description.fingerprint_match',
        'The video was matched to an audio track using fingerprint analysis.',
        'Reason description for when a video was associated via fingerprint',
      );
    case DecisionReason.RELATED_RELEASE_CANDIDATE_MATCH:
      return t(
        'atlas.unassociated_videos.reason_description.related_release_match',
        'The video was matched to an audio track using related release candidate analysis.',
        'Reason description for when a video was associated via related release candidate',
      );
    case DecisionReason.RELATED_RELEASE_EXPANSION_MATCH:
      return t(
        'atlas.unassociated_videos.reason_description.related_release_expansion_match',
        'The video was matched to an audio track using related release expansion analysis.',
        'Reason description for when a video was associated via related release expansion',
      );
    case DecisionReason.FINGER_PRINT_EXPANSION_MATCH:
      return t(
        'atlas.unassociated_videos.reason_description.fingerprint_expansion_match',
        'The video was matched to an audio track using fingerprint expansion analysis.',
        'Reason description for when a video was associated via fingerprint expansion',
      );
    case DecisionReason.UNCATEGORIZED_MATCH:
      return t(
        'atlas.unassociated_videos.reason_description.uncategorized_match',
        'The video was matched to an audio track using an uncategorized matching method.',
        'Reason description for when reason for association is unknown',
      );
    default:
      return EMPTY_VALUE;
  }
};

export const getMetadataAttributeByReason = (
  reason: DecisionReason,
):
  | keyof Pick<TrackDetails, 'name' | 'artists' | 'explicitLyrics' | 'duration'>
  | undefined => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
      return undefined;
    case DecisionReason.TRACK_NAME_MISMATCH:
      return 'name';
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return 'artists';
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return 'explicitLyrics';
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return 'duration';
    default:
      return undefined;
  }
};
